import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — a tiered BIG WIN reached by a cascade, with the cannon multiplying each substep.
 * An L2 cluster wins 1500 × 2 = 3000 and is removed; the collapse drops in an L1 cluster that wins
 * 3000 × 3 = 9000 (total 12000); the final collapse has no win and then presents the big-win
 * overlay. spinWin 12000 @ bet 100 = 120×, so the overlay opens number-only and, counting up,
 * crosses nice(8×) → big(15×: first title + angels) → huge(40×) → epic(≥100×). Final tier "epic" is
 * server-authoritative. The WinMeter counts up 0 → 3000 → 12000. Cluster/mask wins, no payline.
 */
export function baseFixtureReelBigWin(): SpinResponse {
  const win1 = mask([10, 11, 15, 16]);      // L2 ×4 — bottom-left block
  const win2 = mask([5, 6, 10, 11]);        // L1 ×4 — rows 1-2 of cols 0,1 after the first tumble
  const amountMinor = "12000";

  return spinFixture("reel-big-win")
    .balance("EUR", "100000")
    // --- seq 0: reveal — L2 cluster wins 3000, removed ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("3000").gridRows([
        ["L1", "L1", "L2", "H1", "L3"],
        ["L4", "L5", "L5", "L2", "L5"],
        ["L2", "L2", "L4", "L5", "L2"],
        ["L2", "L2", "L3", "L4", "L2"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win1, winAmount: "3000", symbolIdentifier: "L2", symbolCount: "4", // 1500 × 2
        lines: [{ positionMask: win1, winAmount: "3000", baseWinAmount: "1500", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win1" });

      const fire1 = cannonFire(0);
      e.action(fire1.type, fire1.payload, { actionPhaseName: fire1.phase, debugTag: "cannon_fire1" });

      const r = fx.removeSymbolsByMask(win1);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove1" });

      const t = fx.updateTotalWinMeter("3000");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total1" });
    })
    // --- seq 1: collapse — L1 cluster wins another 9000, removed ---
    .event("collapse", 1, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L5", "L3", "L2", "H1", "L3"],
        ["L1", "L1", "L5", "L2", "L5"],
        ["L1", "L1", "L4", "L5", "L2"],
        ["L4", "L5", "L3", "L4", "L2"],
      ]);

      const w = fx.gridWinHighlight({
        positionMask: win2, winAmount: "9000", symbolIdentifier: "L1", symbolCount: "4", // 3000 × 3
        lines: [{ positionMask: win2, winAmount: "9000", baseWinAmount: "3000", multiplierApplied: "3" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win2" });

      const fire2 = cannonFire(1);
      e.action(fire2.type, fire2.payload, { actionPhaseName: fire2.phase, debugTag: "cannon_fire2" });

      const r = fx.removeSymbolsByMask(win2);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove2" });

      const t = fx.updateTotalWinMeter(amountMinor);
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total2" });
    })
    // --- seq 2: collapse — no further win; present the big-win overlay, then end ---
    .event("collapse", 2, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L2", "L5", "L2", "H1", "L3"],
        ["L4", "L3", "L5", "L2", "L5"],
        ["L5", "L3", "L4", "L5", "L2"],
        ["L4", "L5", "L3", "L4", "L2"],
      ]);

      const bw = fx.presentBigWin({ levelIdentifier: "epic", spinWinAmount: amountMinor, betAmount: "100", winRatioLabel: "120x" });
      e.action(bw.type, bw.payload, { actionPhaseName: bw.phase, debugTag: "bigwin" });

      const end = fx.spinBoundaryEnd("base:1", amountMinor);
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
